export interface BatchLoader<Key, Value> {
  load: (key: Key) => Promise<Value>
}

export interface BatchLoaderConfig<Key, Value> {
  fetchChunk: (keys: Key[]) => Promise<Map<string, Value>>
  identify: (key: Key) => string
  /** Fallback for a key the response never mentions, so a pending load can't hang forever. */
  missing: () => Value
  maxBatchSize?: number
  batchWindowMs?: number
}

interface PendingEntry<Key, Value> {
  key: Key
  resolvers: Array<(value: Value) => void>
  rejecters: Array<(error: unknown) => void>
}

/**
 * DataLoader-style: coalesces load() calls placed within a window into a few bulk fetchChunk requests.
 * Create one loader per scope and share it — a per-component instance can't coalesce across components.
 */
export const createBatchLoader = <Key, Value>({
  fetchChunk,
  identify,
  missing,
  maxBatchSize = 50,
  batchWindowMs = 50,
}: BatchLoaderConfig<Key, Value>): BatchLoader<Key, Value> => {
  let pending = new Map<string, PendingEntry<Key, Value>>()
  let scheduled: ReturnType<typeof setTimeout> | null = null

  const runChunk = async (entries: Array<PendingEntry<Key, Value>>) => {
    try {
      const valueById = await fetchChunk(entries.map((entry) => entry.key))

      entries.forEach((entry) => {
        const value = valueById.get(identify(entry.key)) ?? missing()
        entry.resolvers.forEach((resolve) => resolve(value))
      })
    } catch (error) {
      entries.forEach((entry) => entry.rejecters.forEach((reject) => reject(error)))
    }
  }

  const flush = () => {
    scheduled = null
    const entries = Array.from(pending.values())
    pending = new Map()

    for (let start = 0; start < entries.length; start += maxBatchSize) {
      void runChunk(entries.slice(start, start + maxBatchSize))
    }
  }

  const load = (key: Key): Promise<Value> =>
    new Promise<Value>((resolve, reject) => {
      const id = identify(key)
      const entry = pending.get(id)

      if (entry) {
        entry.resolvers.push(resolve)
        entry.rejecters.push(reject)
      } else {
        pending.set(id, { key, resolvers: [resolve], rejecters: [reject] })
      }

      if (scheduled == null) {
        scheduled = setTimeout(flush, batchWindowMs)
      }
    })

  return { load }
}
